// Copyright ©️ 2026 eVolve MEP, LLC

import { type ShipmentItem, useEvolvePatch } from '@evolvemep/api-ts';
import { Flex, Stack, Text } from '@mantine/core';

import { EntityStatusBadge } from 'components/Mantine/EntityStatusBadge';
import { GridMenu } from 'components/Mantine/GridMenu';
import { NumberInputWrapped } from 'components/Mantine/NumberInputWrapped';
import { EditableCell } from 'helpers/ag-grid/EditableCells/EditableCell';
import { formatQuantity } from 'helpers/formatQuantity';
import { isNil } from 'helpers/isNotNil';
import { useUnitOfMeasure } from 'hooks/typeCacheHooks/useUnitOfMeasure';
import { getRemainingItemsToShip, getTaskStatusFromItem } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';
import { WorkOrderItemTypeIcon } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/WorkOrderItemTypeIcon';

type Props = {
  item: ShipmentItem;
  canEdit: boolean;
  onItemUpdated: () => void;
  onDelete: (item: ShipmentItem) => void;
};

export const ShipmentItemRow = ({ item, canEdit, onItemUpdated, onDelete }: Props) => {
  const { uoms } = useUnitOfMeasure();
  const uomCode = uoms?.find((u) => u.unitOfMeasureId === item.unitOfMeasureId)?.code ?? '';

  const { apiCall: patchItemApiCall } = useEvolvePatch(`shop/shipment/${item.shipmentId}/item/${item.shipmentItemId}`);
  const maxQty = getRemainingItemsToShip(item) + item.allocatedQuantity;

  return (
    <Flex align="center" justify="space-between" gap="sm" p="sm" bg="gray.1" style={{ borderRadius: 8 }}>
      <Flex align="center" gap="sm" flex={1}>
        <WorkOrderItemTypeIcon item={item} />
        <Stack gap={0} flex={1}>
          <Text fz="sm">{item.workOrderItemName}</Text>
          <Flex>
            <EditableCell
              Editor={({ onChange, value, onSubmit }) => (
                <NumberInputWrapped
                  label="Qty"
                  w={90}
                  min={1}
                  max={maxQty}
                  clampBehavior="strict"
                  onChange={onChange}
                  value={value}
                  onSubmit={onSubmit}
                  autoFocus
                  data-autofocus
                  allowDecimal={false}
                  description={`Max: ${formatQuantity(maxQty)}`}
                />
              )}
              editable={canEdit}
              initialValue={item.allocatedQuantity}
              required
              onSave={async (value) => {
                if (isNil(value)) return;
                await patchItemApiCall({ allocatedQuantity: value });
                onItemUpdated();
              }}
              className="transparent-editable-cell detail-page-item-thin"
              renderValue={
                <Text fz="sm" c="dimmed">
                  {formatQuantity(item.allocatedQuantity)} {uomCode}
                </Text>
              }
            />
          </Flex>
        </Stack>
      </Flex>
      <Flex align="center" gap="xs">
        <EntityStatusBadge customStatusEntityName="Task" stateName={getTaskStatusFromItem(item)} />
        {canEdit && <GridMenu options={['Delete']} onMenuItemSelected={() => onDelete(item)} />}
      </Flex>
    </Flex>
  );
};
